import React, { useCallback, useRef } from 'react';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart, PieChart, ScatterChart, SankeyChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent, TitleComponent, MarkLineComponent, GraphicComponent } from 'echarts/components';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { FormattedMessage } from 'react-intl';
import { Empty } from 'antd';

import { CHART_COLORS, WHITE } from 'shared/constants/colors';
import theme from 'shared/constants/theme.json';
import useFormatMessage from 'hooks/useFormatMessage';
import ChartToolbox from './ChartToolbox';
import messages from './messages';
import { getGradingScale } from './utils';

echarts.use([
  CanvasRenderer,
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  SankeyChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  TitleComponent,
  MarkLineComponent,
  GraphicComponent,
]);

const DEFAULT_CHART_OPTIONS = {
  color: CHART_COLORS,
  textStyle: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: theme.fontFamily,
  },
};

export const DEFAULT_CHART_HEIGHT = 500;
export const VERTICAL_LEGEND_THRESHOLD = 7;
const GRADING_SCALE_HEIGHT = 50;

const VERTICAL_LEGEND_WIDTH = 200;
const VERTICAL_LEGEND_PRINT_WIDTH = 100;

const Chart = (props) => {
  const {
    toolbox,
    title,
    option,
    isPrint,
    isReport,
    chartSettings,
    onChartSettingsChange,
    alwaysHorizontalLegend,
    onSelectChartSegment,
    className,
    question,
  } = props;
  let { height = DEFAULT_CHART_HEIGHT } = props;

  const $t = useFormatMessage();
  const echartsInstance = useRef();
  const hasData = option?.series?.some(serie => serie.data?.length);

  const saveAsImage = () => {
    const base64Image = echartsInstance.current.getEchartsInstance().getDataURL({
      pixelRatio: 2,
      backgroundColor: WHITE,
    });

    const link = document.createElement('a');

    link.setAttribute('href', base64Image);
    link.setAttribute('download', title?.replace(/[^a-z0-9åäöÅÄÖ-]/gi, '_').replace(/__/, '_').replace(/^_|_$|/g, '') || 'extellio-chart');
    link.click();
  };

  let legendHeight = 0;

  // Vertical legend if too many items
  if (!alwaysHorizontalLegend && option.legend?.data?.length >= VERTICAL_LEGEND_THRESHOLD) {
    option.legend = {
      ...option.legend,
      orient: 'vertical',
      right: 0,
      top: 20,
      bottom: 20,
      textStyle: {
        width: isPrint ? VERTICAL_LEGEND_PRINT_WIDTH : VERTICAL_LEGEND_WIDTH,
        overflow: 'truncate',
      },
      selector: !isPrint ? [{
        type: 'all',
        title: $t(messages.showAll),
      }, {
        type: 'inverse',
        title: $t(messages.invert),
      }] : false,
      selectorLabel: {
        padding: [4, 8],
        borderRadius: 2,
        borderColor: '#d9d9d9',
      },
      emphasis: {
        selectorLabel: {
          backgroundColor: '#1a2f55',
        },
      },
    };

    if (option.grid) {
      option.grid.top = 20;
      option.grid.right = (isPrint ? VERTICAL_LEGEND_PRINT_WIDTH : VERTICAL_LEGEND_WIDTH) + 50;
    }

    legendHeight = option.legend.data.length * 25 + 50;
  } else if (isPrint && option.grid && option.legend?.data) {
    option.grid.top = Math.ceil(option.legend.data.length / 2) * 30 + 20;
  }

  if (chartSettings?.displayGradingScale) {
    const gradingScale = getGradingScale(question, chartSettings, $t);
    if (gradingScale) {
      option.graphic = [gradingScale];
      if (option.grid) {
        option.grid.bottom = (option.grid.bottom || 0) + GRADING_SCALE_HEIGHT;
      }
      height += GRADING_SCALE_HEIGHT;
    }
  }

  const onLegendChange = useCallback(({ selected }) => {
    // Prevent error in echarts due to rerender
    setTimeout(() => {
      onChartSettingsChange?.({ selected });
    }, 0);
  }, [onChartSettingsChange]);

  const onChartClick = useCallback(({ data }) => {
    if (data && onSelectChartSegment) {
      onSelectChartSegment(data);
    }
  }, [onSelectChartSegment]);

  return (
    <div className={`chart ${className ?? ''}`}>
      {hasData ? (
        <ReactEchartsCore
          echarts={echarts}
          style={{ height: Number.isInteger(height) ? Math.max(height, legendHeight) : height }}
          option={{
            animation: !isPrint,
            ...DEFAULT_CHART_OPTIONS,
            ...option,
            legend: {
              ...option.legend,
              selected: chartSettings?.selected || {},
              type: isPrint ? 'plain' : 'scroll',
            },
          }}
          onEvents={{
            legendselectchanged: onLegendChange,
            legendselectall: onLegendChange,
            legendinverseselect: onLegendChange,
            click: onChartClick,
          }}
          notMerge
          ref={echartsInstance}
        />
      ) : (
        <Empty
          height={DEFAULT_CHART_HEIGHT}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<FormattedMessage {...messages[isReport ? 'noMatchingDataReport' : 'noMatchingData']} />}
        />
      )}
      <ChartToolbox {...chartSettings} {...toolbox} question={question} hasData={hasData} saveAsImage={saveAsImage} />
    </div>
  );
};

export default Chart;
